import React, { useCallback, useEffect, useState } from 'react';
import { Alert, App, Button, Skeleton, Typography } from 'antd';
import { Link, useSearchParams } from 'react-router-dom';

import { getEmailPreferences, muteFromLink, turnOffEmailsFromLink } from '../services/emailDelivery';
import { takeLinkToken } from '../utils/linkToken';
import { complaintSentence } from '../utils/emailDelivery';
import { apiErrorMessage } from '../utils/apiErrors';
import { CHECK_CIRCLE_FILL } from '../components/icons';

const { Text } = Typography;

// The link's code survives a reload in the same tab — but never in the
// address bar, history, or a Referer (utils/linkToken.js says why it goes).
const TOKEN_KEY = 'keepr.emailPreferencesToken';

function readToken() {
	const taken = takeLinkToken();
	if (taken) {
		try { window.sessionStorage.setItem(TOKEN_KEY, taken); } catch { /* convenience only */ }
		return taken;
	}
	try { return window.sessionStorage.getItem(TOKEN_KEY) || ''; } catch { return ''; }
}

function Done({ children }) {
	return (
		<p className="email-preferences__done">
			<CHECK_CIRCLE_FILL aria-hidden="true" className="email-preferences__done-mark" />
			<span>{children}</span>
		</p>
	);
}

/**
 * /email-preferences?token=… — where "Manage notification emails" in the foot
 * of every keepr mail lands (docs/plans/email-reputation-and-health.md §4.3).
 *
 * SESSION-LESS, AND ONLY EVER OFF. The link is the person's own durable code;
 * without signing in it may turn their notification emails off, or mute the
 * one notification the mail was about (`?key=` and `?c=` ride on the link) —
 * nothing else. Turning anything on needs a sign-in, and the last panel is the
 * door to that: `/account/notifications?find=<key>`. A leaked mail can
 * therefore silence its own recipient and nothing more (plan D11).
 *
 * The page GETs on load and changes nothing until a button is pressed — mail
 * scanners follow every link. Every dead link (unknown, revoked by an address
 * change or "Sign out everywhere", expired) reads the same: the API answers
 * one flat 404, and so does this page.
 *
 * A spam report's state is explained here too, read-only (plan §3.5): the
 * way back is Resume emails on the account's own Notifications page, which
 * needs a session.
 *
 * Outside the app shell (PublicShell), like /s/:code: the shell's signed-in
 * reads must not fire around a link opened in somebody else's browser.
 */
export default function EmailPreferences() {
	const { message } = App.useApp();
	const [searchParams] = useSearchParams();
	const key = searchParams.get('key') || null;
	const collectionId = searchParams.get('c') || null;
	const [token] = useState(readToken);

	const [state, setState] = useState({ status: token ? 'loading' : 'dead', data: null });
	const [busy, setBusy] = useState(null);

	const load = useCallback(() => {
		if (!token) return;
		setState(prev => ({ ...prev, status: prev.data ? 'ready' : 'loading' }));
		getEmailPreferences(token, { key, collectionId })
			.then(data => setState({ status: 'ready', data }))
			.catch(error => setState({ status: error?.response?.status === 404 ? 'dead' : 'error', data: null }));
	}, [token, key, collectionId]);

	useEffect(() => { load(); }, [load]);

	const act = async (what, fn, done) => {
		setBusy(what);
		try {
			await fn();
			message.success(done);
			load();
		} catch (error) {
			if (error?.response?.status === 404) setState({ status: 'dead', data: null });
			else message.error(apiErrorMessage(error, 'That did not work. Please try again.'));
		} finally {
			setBusy(null);
		}
	};

	const signInPath = `/account/notifications${key ? `?find=${encodeURIComponent(key)}` : ''}`;
	const signIn = (
		<section className="form-panel">
			<div className="micro-label form-panel__kicker">Everything else</div>
			<p className="form-panel__hint">
				Turning emails back on, and choosing notification by notification, needs you to sign in.
			</p>
			<Link to={signInPath}><Button>Sign in to manage everything</Button></Link>
		</section>
	);

	if (state.status === 'loading') {
		return (
			<div className="settings-column email-preferences" data-testid="email-preferences">
				<h1>Email from keepr</h1>
				<Skeleton active />
			</div>
		);
	}

	if (state.status === 'dead') {
		return (
			<div className="settings-column email-preferences" data-testid="email-preferences">
				<h1>Email from keepr</h1>
				<Alert
					type="warning"
					showIcon
					message="This link no longer works"
					description="A link like this stops working when the account's email address changes, or after “Sign out everywhere”. The newest email from keepr carries one that works — or sign in to manage your emails."
				/>
				{signIn}
			</div>
		);
	}

	if (state.status === 'error') {
		return (
			<div className="settings-column email-preferences" data-testid="email-preferences">
				<h1>Email from keepr</h1>
				<Alert
					type="error"
					showIcon
					message="Could not load your email settings"
					action={<Button size="small" onClick={load}>Try again</Button>}
				/>
				{signIn}
			</div>
		);
	}

	const data = state.data || {};
	const on = data.notificationEmails !== false;
	const complained = data.emailDelivery && data.emailDelivery.state === 'complained';
	const notification = data.notification || null;

	return (
		<div className="settings-column email-preferences" data-testid="email-preferences">
			<h1>Email from keepr</h1>
			<p className="email-preferences__to">
				<Text type="secondary">Emails to</Text> <strong>{data.address}</strong>
			</p>

			{complained ? (
				<Alert
					type="info"
					showIcon
					message="keepr has stopped emailing you"
					description={`${complaintSentence(data.emailDelivery, data.address)}, so your notification emails are off. If that was a mistake, sign in and choose Resume emails on your Notifications page.`}
				/>
			) : null}

			<section className="form-panel">
				<div className="micro-label form-panel__kicker">Notification emails</div>
				{on ? (
					<>
						<p className="form-panel__hint">
							Every notification from every collection you are in, by email. Your keepr inbox keeps
							them either way.
						</p>
						<Button
							type="primary"
							loading={busy === 'off'}
							onClick={() => act('off', () => turnOffEmailsFromLink(token), 'Notification emails are off.')}
						>
							Turn off notification emails
						</Button>
					</>
				) : (
					<Done>Notification emails are off. Every notification still reaches your keepr inbox.</Done>
				)}
			</section>

			{notification ? (
				<section className="form-panel">
					<div className="micro-label form-panel__kicker">This notification</div>
					<p className="email-preferences__notification">
						<strong>{notification.name}</strong>
						{notification.collection && notification.collection.name ? <Text type="secondary"> · {notification.collection.name}</Text> : null}
					</p>
					{notification.muted ? (
						<Done>Muted. It still reaches your keepr inbox.</Done>
					) : notification.locked ? (
						<p className="form-panel__hint">
							{notification.collection && notification.collection.name ? notification.collection.name : 'A setting above yours'} requires
							this notification, so it can&rsquo;t be muted.
						</p>
					) : (
						<Button
							loading={busy === 'mute'}
							onClick={() => act('mute', () => muteFromLink(token, { key: notification.key, collectionId: notification.collection ? notification.collection._id : collectionId }), `${notification.name} is muted.`)}
						>
							Mute this notification
						</Button>
					)}
				</section>
			) : null}

			{signIn}
		</div>
	);
}
